import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Star, Instagram, Youtube } from "lucide-react";
import { Logo } from "./Logo";
import { BRAND, NAV_LINKS } from "../lib/site";

export const Footer = () => (
  <footer className="relative overflow-hidden bg-navy-900 text-white" data-testid="site-footer">
    <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full border-[28px] border-gold-500/10" aria-hidden="true" />
    <div className="mx-auto max-w-7xl px-6 pb-10 pt-20 lg:px-10">
      <div className="grid grid-cols-1 gap-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo dark />
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-white/60">
            A decade of trusted brokerage across Ghaziabad, Noida Extension and the NCR — pre-launch investments,
            resale homes and premium rentals.
          </p>
          <div
            className="mt-6 inline-flex items-center gap-3 border border-gold-500/40 bg-white/5 px-4 py-3"
            data-testid="google-rating-badge"
          >
            <div className="flex" aria-hidden="true">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < 4 ? "fill-gold-500 text-gold-500" : "fill-gold-500/70 text-gold-500/70"}`} />
              ))}
            </div>
            <div className="leading-tight">
              <p className="text-sm font-bold text-gold-400">4.9 / 5.0</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Google Reviews</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Follow Estate Paradise on Instagram"
              className="group flex items-center gap-2.5 border border-gold-500/40 bg-white/5 px-4 py-2.5 transition-[border-color,background-color] duration-300 hover:border-gold-400 hover:bg-gold-500/10"
            >
              <Instagram className="h-4 w-4 text-gold-400 transition-transform duration-300 group-hover:scale-110" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 group-hover:text-gold-300">
                Instagram
              </span>
            </a>
            <a
              href={BRAND.youtube}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-youtube-link"
              aria-label="Subscribe to Estate Paradise on YouTube"
              className="group flex items-center gap-2.5 border border-gold-500/40 bg-white/5 px-4 py-2.5 transition-[border-color,background-color] duration-300 hover:border-gold-400 hover:bg-gold-500/10"
            >
              <Youtube className="h-4 w-4 text-gold-400 transition-transform duration-300 group-hover:scale-110" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 group-hover:text-gold-300">
                YouTube
              </span>
            </a>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-gold-500">Explore</p>
          <ul className="mt-6 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-white/65 transition-colors duration-300 hover:text-gold-400"
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/privacy-policy"
                className="text-sm text-white/65 transition-colors duration-300 hover:text-gold-400"
                data-testid="footer-link-privacy-policy"
              >
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-gold-500">Services</p>
          <ul className="mt-6 space-y-3 text-sm text-white/65">
            <li>Pre-Launch Investment Advisory</li>
            <li>Residential Resale Flats</li>
            <li>Premium Rental Management</li>
            <li>Site Visits &amp; Home Loans Assistance</li>
            <li>Legal &amp; Documentation Support</li>
          </ul>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-gold-500">Reach Us</p>
          <ul className="mt-6 space-y-5 text-sm text-white/65">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" />
              <span data-testid="footer-address">{BRAND.address}</span>
            </li>
            <li className="flex gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" />
              <a href={`mailto:${BRAND.email}`} className="transition-colors duration-300 hover:text-gold-400" data-testid="footer-email">
                {BRAND.email}
              </a>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" />
              <a href={`tel:+${BRAND.phoneRaw}`} className="transition-colors duration-300 hover:text-gold-400" data-testid="footer-phone">
                {BRAND.phone}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
        <p className="text-xs tracking-wide text-white/45">
          &copy; {new Date().getFullYear()} Estate Paradise — Real Estate Brokers. All rights reserved.
        </p>
        <Link to="/privacy-policy" className="text-xs tracking-wide text-white/45 transition-colors duration-300 hover:text-gold-400" data-testid="footer-bottom-privacy-link">
          Privacy Policy
        </Link>
        <p className="text-[11px] uppercase tracking-[0.24em] text-gold-500/70">
          Trust &middot; Transparency &middot; NCR
        </p>
      </div>
    </div>
  </footer>
);
